/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    compareVersions,
    isSilentError,
    logDebug,
    logError,
    logInfo,
    logWarn,
    parseVersion,
    requestWithRetry,
    sleep,
    type Version,
} from '@amazon/vinyl-util'

import { ctx } from '@/util/context'
import { bullfrogVersion } from '@/version'
import { toast, toastError } from '@/components/toast'
import { IS_TAURI } from '@/util/platform'

const { target } = ctx('ota')

const UPDATE_BASE_URL = 'https://bullfrog.music.amazon.dev'
// Don't compete with first-load bandwidth
const STARTUP_DELAY_SECONDS = 20
// Keep the active version plus one previous (in case we ever want a rollback).
const KEEP_BUNDLES = 2

/**
 * Schedules a background OTA check on Tauri. No-op on web — the web origin
 * already serves the latest assets on every reload.
 *
 * Update rules:
 * - Only updates within the same major version (1.0.1 → 1.5.x ✅, 1.0.1 → 2.0.0 ✗).
 * - Compares versions with Vinyl's `compareVersions` semantics.
 * - One round-trip: downloads `releases/<version>.zip`, hands the bytes to
 *   Rust which extracts into a sibling `<version>.partial` dir then renames
 *   to the final dir name. Activation is a single tmp-then-rename of the
 *   `bundles/active` pointer.
 * - Toasts when a new bundle is ready. The new version applies on next launch.
 */
export function installOtaUpdater(): void {
    logDebug(target, 'installOtaUpdater')
    if (!IS_TAURI) return

    void (async () => {
        try {
            await sleep(STARTUP_DELAY_SECONDS)
            await checkAndApplyUpdate()
        } catch (e) {
            if (isSilentError(e)) return
            logError(target, e)
            const detail = e instanceof Error ? e.message : String(e)
            toastError(`Update failed: ${detail}`)
        }
    })()
}

async function checkAndApplyUpdate(): Promise<void> {
    const remoteVersionStr = await stage('check version', async () => {
        const res = await requestWithRetry(`${UPDATE_BASE_URL}/version.txt`)
        return (await res.text()).trim()
    })
    const remote = parseVersion(remoteVersionStr)
    if (!isCompatibleUpdate(bullfrogVersion, remote)) {
        logDebug(target, 'no update', { current: bullfrogVersion.str, remote: remote.str })
        return
    }

    // Dynamic import keeps the Tauri runtime in its own lazily-loaded chunk
    // — only fetched once we've actually decided to talk to Rust.
    const { invoke } = await import('@tauri-apps/api/core')

    // If we've already activated this version on a previous run, skip.
    const active = await invoke<string | null>('ota_active_version')
    if (active === remote.str) {
        logDebug(target, 'remote already activated', remote.str)
        return
    }

    logInfo(target, 'downloading update', { current: bullfrogVersion.str, remote: remote.str })

    const zipBytes = await stage(`download ${remote.str}.zip`, async () => {
        const zipUrl = `${UPDATE_BASE_URL}/releases/${remote.str}.zip`
        return (await requestWithRetry(zipUrl)).arrayBuffer()
    })

    // Send raw bytes via Tauri's binary IPC — the JS-side base64 path used
    // String.fromCharCode(...bytes) which blows the call stack on multi-MB
    // bundles. The args slot is reserved for the body, so the version goes
    // through a custom request header instead.
    await stage('install', () =>
        invoke('ota_install_zip', new Uint8Array(zipBytes), {
            headers: { 'x-bullfrog-version': remote.str },
        })
    )
    await stage('activate', () => invoke('ota_activate', { version: remote.str }))

    invoke('ota_prune', { keep: KEEP_BUNDLES }).catch((e) => logWarn(target, 'prune failed', e))

    logInfo(target, 'update ready', remote.str)
    toast(`Updated to v${remote.str}. Restart to apply.`, 6)
}

/**
 * Wraps a step so any thrown error includes the step name. The outer catch
 * surfaces this as `Update failed: <step>: <reason>` in the error toast.
 */
async function stage<T>(name: string, fn: () => Promise<T>): Promise<T> {
    logDebug(target, `stage ${name} start`)
    try {
        return await fn()
    } catch (e) {
        const detail = e instanceof Error ? e.message : String(e)
        throw new Error(`${name}: ${detail}`)
    }
}

function isCompatibleUpdate(current: Version, remote: Version): boolean {
    if (remote.major !== current.major) return false
    return compareVersions(remote, current) > 0
}
